// Advanced Data Export and Sharing Utilities
import { DashboardCard } from '../types';

export interface ExportOptions {
  format: 'pdf' | 'excel' | 'csv' | 'json';
  filename?: string;
  includeCharts?: boolean;
  includeFilters?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export interface ShareOptions {
  type: 'email' | 'link' | 'slack' | 'teams';
  recipients?: string[];
  message?: string;
  permissions?: 'view' | 'edit';
  expiration?: Date;
}

export class DataExportService {
  
  // Export dashboard data to various formats
  static async exportDashboard(
    cards: DashboardCard[], 
    options: ExportOptions
  ): Promise<void> {
    const { format, filename = 'hr-analytics-export', includeCharts = true } = options;
    
    try {
      switch (format) {
        case 'csv':
          await this.exportToCSV(cards, filename);
          break;
        case 'excel':
          await this.exportToExcel(cards, filename, includeCharts);
          break;
        case 'pdf':
          await this.exportToPDF(cards, filename, includeCharts);
          break;
        case 'json':
          await this.exportToJSON(cards, filename);
          break;
        default:
          throw new Error(`Unsupported export format: ${format}`);
      }
      
      // Analytics tracking
      this.trackExport(format, cards.length);
      
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error(`Failed to export to ${format.toUpperCase()}`);
    }
  }

  // CSV Export
  private static async exportToCSV(cards: DashboardCard[], filename: string): Promise<void> {
    const headers = ['Metric', 'Value', 'Change (%)', 'Change Type', 'Description'];
    const rows = cards.map(card => [
      card.title,
      typeof card.value === 'number' ? card.value.toString() : card.value,
      card.change?.toString() || '',
      card.changeType || '',
      card.description || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    this.downloadFile(blob, `${filename}.csv`);
  }

  // Excel Export (simulated - in real app would use library like xlsx)
  private static async exportToExcel(
    cards: DashboardCard[], 
    filename: string, 
    includeCharts: boolean
  ): Promise<void> {
    // In a real implementation, you'd use a library like 'xlsx' or 'exceljs'
    // For now, we'll create a structured JSON that could be converted to Excel
    
    const workbookData = {
      sheets: [
        {
          name: 'Dashboard Metrics',
          data: cards.map(card => ({
            'Metric': card.title,
            'Value': card.value,
            'Change (%)': card.change,
            'Change Type': card.changeType,
            'Description': card.description,
            'Timestamp': new Date().toISOString()
          }))
        }
      ],
      includeCharts,
      metadata: {
        exportDate: new Date().toISOString(),
        totalMetrics: cards.length,
        source: 'HR Analytics Platform'
      }
    };

    const jsonString = JSON.stringify(workbookData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    this.downloadFile(blob, `${filename}.xlsx.json`); // Temporary format
  }

  // PDF Export (would use jsPDF or similar in real implementation)
  private static async exportToPDF(
    cards: DashboardCard[], 
    filename: string, 
    includeCharts: boolean
  ): Promise<void> {
    // Create a simplified HTML structure for PDF generation
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>HR Analytics Report</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; margin: 40px; }
            .header { border-bottom: 2px solid #0078d4; padding-bottom: 20px; margin-bottom: 30px; }
            .metric { margin-bottom: 20px; padding: 15px; border: 1px solid #e5e7eb; border-radius: 8px; }
            .metric-title { font-weight: 600; color: #1e293b; margin-bottom: 5px; }
            .metric-value { font-size: 24px; font-weight: 700; color: #0078d4; margin-bottom: 5px; }
            .metric-change { font-size: 14px; }
            .positive { color: #16a34a; }
            .negative { color: #dc2626; }
            .footer { margin-top: 40px; text-align: center; color: #6b7280; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>HR Analytics Dashboard Report</h1>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          ${cards.map(card => `
            <div class="metric">
              <div class="metric-title">${card.title}</div>
              <div class="metric-value">${card.value}</div>
              ${card.change !== undefined ? `
                <div class="metric-change ${card.changeType || 'neutral'}">
                  ${card.changeType === 'positive' ? '↗' : card.changeType === 'negative' ? '↘' : '→'} 
                  ${Math.abs(card.change)}%
                </div>
              ` : ''}
              ${card.description ? `<div class="metric-description">${card.description}</div>` : ''}
            </div>
          `).join('')}
          <div class="footer">
            <p>HR Analytics Platform - Confidential Report</p>
          </div>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    this.downloadFile(blob, `${filename}.html`); // Temporary format - would convert to PDF
  }

  // JSON Export
  private static async exportToJSON(cards: DashboardCard[], filename: string): Promise<void> {
    const exportData = {
      version: '1.0',
      exportDate: new Date().toISOString(),
      source: 'HR Analytics Platform',
      metrics: cards.map(card => ({
        id: card.id,
        title: card.title,
        value: card.value,
        change: card.change,
        changeType: card.changeType,
        description: card.description,
        color: card.color,
        icon: card.icon
      }))
    };

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    this.downloadFile(blob, `${filename}.json`);
  }

  // File download utility
  private static downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Share dashboard functionality
  static async shareDashboard(
    cards: DashboardCard[], 
    options: ShareOptions
  ): Promise<string | void> {
    const { type, recipients, message, permissions = 'view' } = options;

    try {
      switch (type) {
        case 'email':
          return await this.shareViaEmail(cards, recipients, message);
        case 'link':
          return await this.generateShareLink(cards, permissions, options.expiration);
        case 'slack':
          return await this.shareToSlack(cards, message);
        case 'teams':
          return await this.shareToTeams(cards, message);
        default:
          throw new Error(`Unsupported share type: ${type}`);
      }
    } catch (error) {
      console.error('Sharing failed:', error);
      throw new Error(`Failed to share via ${type}`);
    }
  }

  // Email sharing
  private static async shareViaEmail(
    cards: DashboardCard[], 
    recipients?: string[], 
    message?: string
  ): Promise<void> {
    const subject = 'HR Analytics Dashboard - Shared Report';
    const body = `${message || 'Please find the HR Analytics dashboard report below:'}\n\n` +
      cards.map(card => 
        `${card.title}: ${card.value}${card.change ? ` (${card.change > 0 ? '+' : ''}${card.change}%)` : ''}`
      ).join('\n') +
      `\n\nGenerated on ${new Date().toLocaleDateString()}`;

    const mailtoUrl = `mailto:${recipients?.join(',') || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  }

  // Generate shareable link
  private static async generateShareLink(
    cards: DashboardCard[], 
    permissions: string, 
    expiration?: Date
  ): Promise<string> {
    // In a real implementation, this would create a secure share token
    const shareData = {
      cards: cards.map(c => ({ id: c.id, title: c.title, value: c.value })),
      permissions,
      expiration: expiration?.toISOString(),
      createdAt: new Date().toISOString()
    };

    // Simulate API call to create share link
    const shareToken = btoa(JSON.stringify(shareData)).slice(0, 32);
    const shareUrl = `${window.location.origin}/shared/${shareToken}`;
    
    // Copy to clipboard
    await navigator.clipboard.writeText(shareUrl);
    
    return shareUrl;
  }

  // Slack integration
  private static async shareToSlack(cards: DashboardCard[], message?: string): Promise<void> {
    const slackMessage = {
      text: message || 'HR Analytics Dashboard Update',
      blocks: [
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*HR Analytics Dashboard*\n${message || 'Latest metrics:'}`
          }
        },
        {
          type: 'divider'
        },
        ...cards.slice(0, 5).map(card => ({
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*${card.title}*\n${card.value}`
            },
            {
              type: 'mrkdwn',
              text: card.change ? `*Change*\n${card.change > 0 ? '+' : ''}${card.change}%` : ' '
            }
          ]
        }))
      ]
    };

    // In a real app, this would use Slack Web API
    console.log('Slack message payload:', slackMessage);
    alert('Slack integration not implemented in demo. Check console for payload.');
  }

  // Microsoft Teams integration
  private static async shareToTeams(cards: DashboardCard[], message?: string): Promise<void> {
    const teamsMessage = {
      '@type': 'MessageCard',
      '@context': 'http://schema.org/extensions',
      summary: 'HR Analytics Dashboard Update',
      title: 'HR Analytics Dashboard',
      text: message || 'Latest HR metrics and insights',
      sections: [
        {
          facts: cards.slice(0, 6).map(card => ({
            name: card.title,
            value: `${card.value}${card.change ? ` (${card.change > 0 ? '+' : ''}${card.change}%)` : ''}`
          }))
        }
      ]
    };

    // In a real app, this would use Teams webhook or Bot Framework
    console.log('Teams message payload:', teamsMessage);
    alert('Teams integration not implemented in demo. Check console for payload.');
  }

  // Export drilldown data
  static async exportDrilldownData(data: any, kpiType: string): Promise<void> {
    const filename = `${kpiType}-drilldown-${new Date().toISOString().split('T')[0]}`;
    
    // Convert drilldown data to CSV format
    if (data.chart === 'table' && data.data) {
      const tableData = data.data.employees || 
                       data.data.roles || 
                       data.data.requisitions || 
                       data.data.breakdown ||
                       data.data.questions;
      
      if (tableData && tableData.length > 0) {
        const headers = Object.keys(tableData[0]);
        const rows = tableData.map((row: any) => 
          headers.map(header => {
            const value = row[header];
            return Array.isArray(value) ? value.join('; ') : value;
          })
        );
        
        const csvContent = [
          headers.join(','),
          ...rows.map((row: any[]) => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        this.downloadFile(blob, `${filename}.csv`);
      }
    } else if (data.data) {
      // Export chart data
      const exportData = data.data.map((item: any) => ({
        Label: item.name || item.type || item.category || item.tenure || item.criticality || item.grade,
        Value: item.value || item.score || item.days || item.readiness || item.risk || item.count,
        ...(item.trend && { Trend: item.trend }),
        ...(item.percentage && { Percentage: `${item.percentage}%` }),
        ...(item.factors && { Factors: item.factors.join('; ') }),
        ...(item.departments && { Departments: item.departments }),
        ...(item.pipeline && { Pipeline: item.pipeline }),
        ...(item.efficiency && { Efficiency: item.efficiency })
      }));
      
      await this.exportToCSV(exportData.map((item: any) => ({
        id: '',
        title: item.Label,
        value: item.Value,
        change: item.Trend,
        description: Object.entries(item).filter(([key]) => !['Label', 'Value', 'Trend'].includes(key))
          .map(([key, value]) => `${key}: ${value}`).join(', ')
      })), filename);
    }
    
    this.trackExport('drilldown', 1);
  }

  // Analytics tracking
  private static trackExport(format: string, itemCount: number): void {
    // In a real app, this would send analytics data
    console.log(`Export analytics: ${format} format, ${itemCount} items`);
  }

  // Bulk export with progress tracking
  static async bulkExport(
    datasets: { name: string; cards: DashboardCard[] }[],
    format: ExportOptions['format'],
    onProgress?: (progress: number) => void
  ): Promise<void> {
    const total = datasets.length;
    
    for (let i = 0; i < total; i++) {
      const { name, cards } = datasets[i];
      await this.exportDashboard(cards, {
        format,
        filename: `${name}-${format}-export`,
      });
      
      onProgress?.((i + 1) / total * 100);
      
      // Small delay to prevent overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
}

export default DataExportService;